import { EMAIL_REGEXP, DEFAULT_PLUS_DISCOUNT_PERCENTAGE } from './constants';
import { SessionObjectProps } from '~/types/user';

interface Cookie {
  key: string;
  value: string;
}

export const isValidEmail = (email: string): boolean =>
  EMAIL_REGEXP.test(String(email).toLowerCase());

export function removeHTMLTags(str: string): string {
  let cleanedString = '';

  if (str === null || str === '') return '';
  else cleanedString = str.toString();

  // Regex to replace the identified HTML tag with an empty string.
  return cleanedString.replace(/(<([^>]+)>)/gi, '');
}

export const getCSRFCookie = (): string => {
  if (!process.browser) {
    return '';
  }

  const cookie = document.cookie
    .split(';')
    .map(
      (c): Cookie => ({
        key: c.split('=')?.[0]?.trim(),
        value: c.split('=')?.[1]?.trim(),
      }),
    )
    .find((c): boolean => c.key === 'csrftoken');

  return cookie ? cookie.value : '';
};

/**
 * @param text The HTML string to parse
 * @param maxLength The maximum length of the returned string
 * @param minLength The minimum length of the returned string
 *
 * @returns The first sentence of the given text
 */
export const firstSentence = (
  text: string,
  maxLength = 60,
  minLength = 8,
): string => {
  if (text.length <= minLength) {
    return text;
  }

  // find first sentence with a minimum of minLength chars
  for (
    let i = Math.min(text.length, minLength);
    i < Math.min(text.length, maxLength);
    i += 1
  ) {
    const c = text.charAt(i);

    if (['?', '.', '!'].includes(c)) {
      return text.substr(0, i + 1);
    }
  }

  // no separator found, return full string
  if (text.length < maxLength) {
    return text;
  }

  // no sentence found, stop at last word prior to maxLength
  const i = text.lastIndexOf(' ', maxLength);
  if (i > 0) {
    return text.substr(0, i);
  }

  // last resort just trim maxLength
  return text.substr(0, maxLength);
};

/**
 * Get UTM params for social share modal.
 * @param {Object} data - Deal or wishlist data
 * @param {String} data.slug - Deal or wishlist slug
 * @param {Number} data.id - Deal or wishlist id
 * @param {Number} userId - Id of the logged in user
 * @returns {Object} An object with the social share utm params
 */
export const getUtmParamsForSocialShare = ({
  data,
  userId,
}: {
  data: { slug: string; id: number };
  userId: number | undefined;
}) => {
  const utmMedium = 'utm_medium=social';
  const getUtmSource = (social: string) =>
    `utm_source=${data.slug}-share-${social}`;
  const getUtmCampaign = (social: string) => {
    const userIdString = userId ? `-${userId}` : '';
    return `utm_campaign=${data.slug}-share-${social}-${data.id}${userIdString}`;
  };

  return {
    text: `?${getUtmSource('link')}&${utmMedium}&${getUtmCampaign('link')}`,
    email: `?${getUtmSource('email')}&${utmMedium}&${getUtmCampaign('email')}`,
    facebook: `?${getUtmSource('facebook')}&${utmMedium}&${getUtmCampaign(
      'facebook',
    )}`,
    linkedin: `?${getUtmSource('linkedin')}&${utmMedium}&${getUtmCampaign(
      'linkedin',
    )}`,
    twitter: `?${getUtmSource('twitter')}&${utmMedium}&${getUtmCampaign(
      'twitter',
    )}`,
  };
};

/**
 * Open a new window with the given url and parameters
 * @param {String} url - The url to open
 * @param {String} windowName - The name of the window
 * @param {Boolean} popup - Whether to open the window as a popup
 * @param {Number} height - The height of the window
 * @param {Number} width - The width of the window
 * @returns {void}
 */
export const openExternalWindow = ({
  url,
  windowName,
  popup,
  height = 600,
  width = 600,
}: {
  url: string;
  windowName: string;
  popup: boolean;
  height?: number;
  width?: number;
}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;
  const windowFeatures = [
    `left=${left}`,
    `top=${top}`,
    `width=${width}`,
    `height=${height}`,
    `popup=${popup}`,
  ].join(',');
  window.open(url, windowName, windowFeatures);
};
// Useful for hidden fields with string values
export const isTrue = (value: string) => value.toLowerCase() === 'true';
export const isFalse = (value: string) => value.toLowerCase() === 'false';
export const getPlusDiscount = (session?: SessionObjectProps) =>
  `${session?.plus_percentage_off ?? DEFAULT_PLUS_DISCOUNT_PERCENTAGE}`;
